<script lang="ts" setup>
import { computed, useSlots } from 'vue';
import TargetComponent from '@/assets/svg/target.svg?component';
import ExpandIconComponent from '@/assets/svg/expand-icon.svg?component';
import ScalesComponent from '@/assets/svg/scales.svg?component';
import InfoCircleComponent from '@/assets/svg/info-circle.svg?component';
import ThreeStarPlusIconComponent from '@/assets/svg/3-star-plus-icon.svg?component';
import FourStarPlusIconComponent from '@/assets/svg/4-star-plus-icon.svg?component';
import HeartIconComponent from '@/assets/svg/heart.svg?component';
import BriefcaseIconComponent from '@/assets/svg/briefcase.svg?component';
import BoltFastIconComponent from '@/assets/svg/bolt-fast.svg?component';
import CheckmarkIconComponent from '@/assets/svg/checkmark.svg?component';
import ClockIconComponent from '@/assets/svg/clock.svg?component';
import SunIconComponent from '@/assets/svg/sun.svg?component';
import ListIconComponent from '@/assets/svg/list.svg?component';
import MagnifyingGlassIconComponent from '@/assets/svg/magnifying-glass.svg?component';
import TaskCheckIconComponent from '@/assets/svg/task-check.svg?component';
import MegaphoneIconComponent from '@/assets/svg/megaphone.svg?component';
import StackIconComponent from '@/assets/svg/stack.svg?component';
import SingleStackItemIconComponent from '@/assets/svg/single-stack-item.svg?component';
import RefreshIconComponent from '@/assets/svg/refresh-horizontal.svg?component';
import RefreshLockedIconComponent from '@/assets/svg/refresh-locked.svg?component';
import DiveDeeperIconComponent from '@/assets/svg/dive-deeper.svg?component';
import CheckedListItemsIconComponent from '@/assets/svg/checked-list-items.svg?component';
import NoCircleIconComponent from '@/assets/svg/no-circle.svg?component';

const props = defineProps<{
  title: string;
  subtitle?: string;
  selectedCalloutFullWidth?: boolean;
  items: {
    title: string;
    subtitle: string;
    icon:
      | 'target'
      | 'expand'
      | 'scale'
      | 'three-star'
      | 'four-star'
      | 'heart'
      | 'briefcase'
      | 'bolt-fast'
      | 'checkmark'
      | 'clock'
      | 'sun'
      | 'list'
      | 'magnifying-glass'
      | 'task-check'
      | 'megaphone'
      | 'stack'
      | 'single-stack-item'
      | 'refresh'
      | 'refresh-locked'
      | 'dive-deeper'
      | 'checked-list-items'
      | 'no-circle';
    isSelected: boolean;
    showCallout?: boolean;
    tooltipText?: string;
    value: string;
  }[];
  titleClass?: string;
}>();

const emit = defineEmits<{
  (e: 'click:select', item: string): void;
}>();
const slots = useSlots();

const titleText = computed(() => props.title);
const subtitleText = computed(() => props.subtitle ?? '');
const hasSubtitle = computed(() => subtitleText.value.trim().length > 0);
const items = computed(() => props.items);

const handleClick = (item: { value: string }) => {
  emit('click:select', item.value);
};

const hasSelectedCallout = (item: { isSelected: boolean; showCallout?: boolean }) => {
  return item.isSelected && Boolean(item.showCallout) && Boolean(slots['selected-callout']);
};

const showIndentedSelectedCallout = (item: { isSelected: boolean; showCallout?: boolean }) => {
  return hasSelectedCallout(item) && !props.selectedCalloutFullWidth;
};

const showFullWidthSelectedCallout = (item: { isSelected: boolean; showCallout?: boolean }) => {
  return hasSelectedCallout(item) && Boolean(props.selectedCalloutFullWidth);
};

const getIcon = (
  icon:
    | 'target'
    | 'expand'
    | 'scale'
    | 'three-star'
    | 'four-star'
    | 'heart'
    | 'briefcase'
    | 'bolt-fast'
    | 'checkmark'
    | 'clock'
    | 'sun'
    | 'list'
    | 'magnifying-glass'
    | 'task-check'
    | 'megaphone'
    | 'stack'
    | 'single-stack-item'
    | 'refresh'
    | 'refresh-locked'
    | 'dive-deeper'
    | 'checked-list-items'
    | 'no-circle',
) => {
  switch (icon) {
    case 'target':
      return TargetComponent;
    case 'expand':
      return ExpandIconComponent;
    case 'scale':
      return ScalesComponent;
    case 'three-star':
      return ThreeStarPlusIconComponent;
    case 'four-star':
      return FourStarPlusIconComponent;
    case 'heart':
      return HeartIconComponent;
    case 'briefcase':
      return BriefcaseIconComponent;
    case 'bolt-fast':
      return BoltFastIconComponent;
    case 'checkmark':
      return CheckmarkIconComponent;
    case 'clock':
      return ClockIconComponent;
    case 'sun':
      return SunIconComponent;
    case 'list':
      return ListIconComponent;
    case 'magnifying-glass':
      return MagnifyingGlassIconComponent;
    case 'task-check':
      return TaskCheckIconComponent;
    case 'megaphone':
      return MegaphoneIconComponent;
    case 'stack':
      return StackIconComponent;
    case 'single-stack-item':
      return SingleStackItemIconComponent;
    case 'refresh':
      return RefreshIconComponent;
    case 'refresh-locked':
      return RefreshLockedIconComponent;
    case 'dive-deeper':
      return DiveDeeperIconComponent;
    case 'checked-list-items':
      return CheckedListItemsIconComponent;
    case 'no-circle':
      return NoCircleIconComponent;
    default:
      return null;
  }
};

const hasLargeIcon = computed(() => {
  return items.value.some((item) => item.icon === 'three-star' || item.icon === 'four-star');
});
</script>

<template>
  <div class="cursor-pointer">
    <!-- Title -->
    <div
      :class="[
        'text-shade-88 font-sans text-lg font-extrabold leading-[27px]',
        { 'mb-3': !hasSubtitle },
        titleClass,
      ]"
    >
      {{ titleText }}
    </div>

    <!-- Subtitle -->
    <div v-if="hasSubtitle" class="mb-3 text-sm leading-[21px] text-shade-800">
      {{ subtitleText }}
    </div>

    <!-- Items -->
    <div
      v-for="item in items"
      :key="item.value"
      class="mb-4 rounded-md border px-4 transition-colors"
      :class="{
        'border-2 border-highlight-500': item.isSelected,
        'border border-tint-80': !item.isSelected,
        'hover:border-shade-840': !item.isSelected,
        'flex h-[72px] items-center py-[19px]': !hasSelectedCallout(item),
        'flex items-start py-4': showIndentedSelectedCallout(item),
        'flex flex-col py-4': showFullWidthSelectedCallout(item),
      }"
      @click="handleClick(item)"
    >
      <div
        class="flex w-full"
        :class="{
          'items-start': showIndentedSelectedCallout(item),
          'items-center': !showIndentedSelectedCallout(item),
        }"
      >
        <!-- Icon -->
        <div
          class="mr-3 flex h-10 items-center justify-center rounded-md"
          :class="{
            'bg-tint-20': !item.isSelected,
            'bg-[#F7EAFF]': item.isSelected,
            'w-10': !hasLargeIcon,
          }"
        >
          <component
            :is="getIcon(item.icon)"
            :class="{
              'text-tint-400': !item.isSelected,
              'text-highlight-500': item.isSelected,
            }"
          />
        </div>

        <!-- Title and Subtitle -->
        <div class="mx-4 flex-grow font-sans text-sm leading-[14px]">
          <span class="mb-1.5 block font-extrabold text-shade-900">
            {{ item.title }}
          </span>
          <span class="block font-normal text-shade-820">
            {{ item.subtitle }}
          </span>
          <div v-if="showIndentedSelectedCallout(item)" class="mt-3">
            <slot name="selected-callout" :item="item"></slot>
          </div>
        </div>

        <!-- Tooltip -->
        <div v-show="item.tooltipText">
          <InfoCircleComponent class="h-4 w-4 text-shade-800" />
          <v-tooltip
            activator="parent"
            location="right"
            max-width="300"
            content-class="bg-white shadow-md text-shade-880 !text-sm !py-2 !px-3 !rounded-md"
          >
            <strong>Example: </strong>{{ item.tooltipText }}
          </v-tooltip>
        </div>
      </div>

      <div v-if="showFullWidthSelectedCallout(item)" class="mt-3 w-full">
        <slot name="selected-callout" :item="item"></slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
